"use client";

import React from "react";
import { ShieldCheck, ScanLine, ArrowRight, CircleCheck as CheckCircle2, Award, Microscope, FileCheck2, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const stats = [{
  id: 1,
  value: "Raw Material",
  label: "Verification Focus"
}, {
  id: 2,
  value: "< 15s",
  label: "Rapid In-Line Testing"
}, {
  id: 3,
  value: "ISO / GMP",
  label: "Compliance Ready"
}];
const featurePills = [{
  id: 1,
  title: "Non-destructive testing"
}, {
  id: 2,
  title: "Batch traceability"
}, {
  id: 3,
  title: "Adulterant detection"
}, {
  id: 4,
  title: "Moisture & fat analysis"
}];
export default function Traceability() {
  return <section id="foodtraceabilityhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Subtle organic background decoration */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 -z-10 size-96 rounded-full bg-secondary/70 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/4 -z-10 size-80 rounded-full bg-muted/60 blur-3xl" />

      <div className="page-container">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Core Narrative */}
          <div className="lg:col-span-7">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
              <Link to="/IndustriesPage" className="transition-colors hover:text-foreground">
                Industries
              </Link>
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
              <span className="font-medium text-foreground" aria-current="page">
                Food and Ingredients
              </span>
            </nav>

            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-secondary-foreground border border-border">
                <ShieldCheck className="size-3.5 text-primary" />
                Food &amp; Ingredients Quality Assurance
              </Badge>
            </div>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Reliable verification, complete traceability, uncompromised food safety.
            </h1>

            <p className="mt-6 text-lg text-muted-foreground leading-relaxed sm:text-xl">
              Equip your food production line and QC laboratories with analytical instrumentation built to authenticate raw ingredients, prevent adulteration, and guarantee regulatory compliance across the UK and Ireland.
            </p>

            {/* Quick feature tags */}
            <div className="mt-8 flex flex-wrap gap-2.5">
              {featurePills.map((pill, i) => <div key={pill.id} data-index={i} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-medium text-secondary-foreground">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span>{pill.title}</span>
                </div>)}
            </div>

            {/* Call to Actions */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md font-semibold shadow-sm">
                <Link to="/ContactPage">
                  <span>Speak with a Specialist</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/SolutionsPage">
                  <ScanLine className="size-4 text-muted-foreground" />
                  <span>Explore Analytical Systems</span>
                </Link>
              </Button>
            </div>

            {/* Credibility Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4 border-t border-border pt-8 sm:gap-6">
              {stats.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col">
                  <span className="text-2xl font-bold text-foreground sm:text-3xl">
                    {stat.value}
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground sm:text-sm font-medium">
                    {stat.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Right Column: Imagery & Highlight Cards */}
          <div className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              
              {/* Main Visual Frame */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/food_traceability_laboratory.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/food_traceability_laboratory.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/food_traceability_laboratory.png-wvc-sizes" alt="Food safety specialist conducting analytical ingredient verification in laboratory" loading="lazy" decoding="async" className="h-80 w-full object-cover sm:h-96 md:h-[420px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/10 to-transparent" />
                
                {/* Overlay Caption on Image */}
                <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-card/95 p-4 backdrop-blur-sm border border-border">
                  <div className="flex items-start gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Microscope className="size-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-card-foreground">
                        Ingredient Authenticity Guaranteed
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Instant spectral verification from grain intake to finished packaging.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Quality Stamp Card */}
              <div className="absolute -bottom-6 -left-6 hidden sm:flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-xl">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                  <Award className="size-5 text-primary" />
                </div>
                <div>
                  <span className="block text-xs font-semibold text-card-foreground">
                    Regulated Industry Support
                  </span>
                  <span className="block text-2xs text-muted-foreground">
                    Ireland &amp; UK Qualified Service
                  </span>
                </div>
              </div>

              {/* Floating Certificate Card */}
              <div className="absolute -top-5 -right-5 hidden sm:flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-md">
                <FileCheck2 className="size-4 text-primary" />
                <span className="text-xs font-medium text-card-foreground">
                  Full Audit Trail &amp; Data Integrity
                </span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}